import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { REQUIREMENT_OPTIONS } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { name: "", phone: "", email: "", requirement: "", message: "" };
const inputCls =
  "w-full border border-line bg-ink px-4 py-3.5 text-sm text-white placeholder:text-dim outline-none transition-colors duration-200 focus:border-edge";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-ash";

const useEnquiryForm = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! Our team will reach out to you shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Could not send your enquiry. Please WhatsApp or call us instead.");
    } finally {
      setSending(false);
    }
  };
  return { form, set, submit, sending };
};

const Field = ({ id, label, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>
      {label}
    </label>
    {children}
  </div>
);

export const ContactForm = () => {
  const { form, set, submit, sending } = useEnquiryForm();
  return (
    <form
      onSubmit={submit}
      className="clip-corner border border-line bg-card p-7 sm:p-9"
      data-testid="contact-enquiry-form"
    >
      <p className="mb-7 font-mono text-[11px] uppercase tracking-[0.3em] text-dim">Send an Enquiry</p>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="enquiry-name" label="Name *">
          <input id="enquiry-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} data-testid="contact-input-name" />
        </Field>
        <Field id="enquiry-phone" label="Phone Number *">
          <input id="enquiry-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="Your phone number" className={inputCls} data-testid="contact-input-phone" />
        </Field>
        <Field id="enquiry-email" label="Email">
          <input id="enquiry-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="contact-input-email" />
        </Field>
        <Field id="enquiry-requirement" label="Requirement *">
          <select
            id="enquiry-requirement"
            required
            value={form.requirement}
            onChange={set("requirement")}
            className={`${inputCls} appearance-none ${form.requirement ? "" : "text-dim"}`}
            data-testid="contact-select-requirement"
          >
            <option value="" disabled>
              Select requirement
            </option>
            {REQUIREMENT_OPTIONS.map((o) => (
              <option key={o} value={o} className="bg-ink text-white">
                {o}
              </option>
            ))}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <label htmlFor="enquiry-message" className={labelCls}>
            Message *
          </label>
          <textarea
            id="enquiry-message"
            required
            rows={4}
            value={form.message}
            onChange={set("message")}
            placeholder="Tell us about your space, goals and what you're looking for"
            className={`${inputCls} resize-none`}
            data-testid="contact-input-message"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-form-submit-button"
        className="clip-corner-sm mt-7 flex w-full items-center justify-center gap-3 bg-edge px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-edgehot disabled:opacity-60"
      >
        {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
        {sending ? "Sending…" : "Send Enquiry"}
      </button>
    </form>
  );
};
